import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Clock, 
  MapPin, 
  CheckCircle, 
  ChefHat, 
  Package, 
  ArrowLeft,
  Star,
  Send,
  Download,
  FileText
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format pickup time - handles both "HH:MM" and ISO formats
const formatPickupTime = (pickupSlot, pickupTime) => {
  const timeValue = pickupSlot || pickupTime;
  if (!timeValue) return 'À définir';
  
  // Check if it's just a time format (HH:MM)
  if (/^\d{1,2}:\d{2}$/.test(timeValue)) {
    return `Aujourd'hui à ${timeValue}`;
  }
  
  // Try to parse as a full date
  const date = new Date(timeValue);
  if (!isNaN(date.getTime())) {
    return date.toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  // Fallback
  return timeValue || 'À définir';
};

const statusConfig = {
  'Reçue': { icon: CheckCircle, color: 'bg-orange-100 text-orange-700', step: 1 },
  'En préparation': { icon: ChefHat, color: 'bg-blue-100 text-blue-700', step: 2 },
  'Prête': { icon: Package, color: 'bg-emerald-100 text-emerald-700', step: 3 },
  'Terminée': { icon: CheckCircle, color: 'bg-gray-100 text-gray-700', step: 4 },
  'Annulée': { icon: CheckCircle, color: 'bg-red-100 text-red-700', step: 0 },
};

// Star Rating Component
function StarRating({ rating, setRating, readonly = false, size = 'md' }) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && setRating(star)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        >
          <Star
            className={`${sizes[size]} ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [, setPreviousStatus] = useState(null);
  
  // Review states
  const [canReview, setCanReview] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  const getStatusMessage = useCallback((status) => {
    const messages = {
      'Reçue': 'Le restaurant a reçu votre commande',
      'En préparation': 'Votre commande est en cours de préparation',
      'Prête': 'Votre commande est prête à être récupérée !',
      'Terminée': 'Merci pour votre commande',
      'Annulée': 'Votre commande a été annulée'
    };
    return messages[status] || '';
  }, []);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders/${id}`);
      const newOrder = response.data;
      
      // Check if status changed and notify
      setPreviousStatus((prev) => {
        if (prev && prev !== newOrder.status) {
          toast.success(`Statut mis à jour: ${newOrder.status}`, {
            description: getStatusMessage(newOrder.status),
            duration: 5000,
          });
        }
        return newOrder.status;
      });
      
      setOrder(newOrder);
      setLastUpdate(new Date());
    } catch {
      toast.error('Commande non trouvée');
      navigate('/mon-compte');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, getStatusMessage]);

  const checkCanReview = useCallback(async () => {
    if (!order?.restaurant_id) return;
    try {
      const response = await axios.get(`${API}/reviews/can-review/${order.restaurant_id}`);
      setCanReview(response.data.can_review);
      if (response.data.review) {
        setExistingReview(response.data.review);
      }
    } catch {
      console.error('Error checking review status');
    }
  }, [order?.restaurant_id]);

  useEffect(() => {
    fetchOrder();
    // Poll for updates every 10 seconds for real-time experience
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  useEffect(() => {
    if (order?.restaurant_id) {
      checkCanReview();
    }
  }, [order?.restaurant_id, checkCanReview]);

  const handleSubmitReview = async () => {
    if (reviewRating < 1) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    
    if (!order?.restaurant_id) {
      toast.error('Erreur: restaurant non trouvé');
      return;
    }
    
    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        restaurant_id: order.restaurant_id,
        order_id: id,
        rating: reviewRating,
        comment: reviewComment || null
      });
      
      toast.success('Merci pour votre avis !');
      setExistingReview(response.data);
      setCanReview(false);
    } catch (_error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'envoi de l'avis"));
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const response = await axios.get(`${API}/orders/${id}/receipt`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recu_${order?.order_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reçu téléchargé avec succès');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Erreur lors du téléchargement du reçu');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-32 bg-gray-200 rounded-2xl" />
            <div className="h-48 bg-gray-200 rounded-2xl" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) return null;

  const currentStatus = statusConfig[order.status] || statusConfig['Reçue'];
  const StatusIcon = currentStatus.icon;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Live Indicator */}
        {order.status !== 'Terminée' && order.status !== 'Annulée' && (
          <div className="flex items-center justify-center gap-2 mb-4 py-2 bg-emerald-50 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-sm font-medium text-emerald-700">En direct</span>
            <span className="text-xs text-emerald-600">
              • Mis à jour {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Commande {order.order_number}</h1>
            <p className="text-sm text-gray-500">
              {new Date(order.created_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 rounded-full ${currentStatus.color} flex items-center justify-center`}>
              <StatusIcon className="w-7 h-7" />
            </div>
            <div>
              <Badge className={currentStatus.color}>{order.status}</Badge>
              <p className="text-gray-600 mt-1">
                {order.status === 'Reçue' && 'Votre commande a été reçue par le restaurant'}
                {order.status === 'En préparation' && 'Le restaurant prépare votre commande'}
                {order.status === 'Prête' && 'Votre commande est prête! Venez la récupérer'}
                {order.status === 'Terminée' && 'Commande récupérée. Merci!'}
                {order.status === 'Annulée' && 'Cette commande a été annulée'}
              </p>
            </div>
          </div>

          {/* Progress Timeline */}
          {order.status !== 'Annulée' && (
            <div className="relative">
              <div className="flex justify-between">
                {['Reçue', 'En préparation', 'Prête', 'Terminée'].map((status, index) => {
                  const stepNum = index + 1;
                  const isActive = currentStatus.step >= stepNum;
                  const isCurrent = currentStatus.step === stepNum;
                  
                  return (
                    <div key={status} className="flex flex-col items-center flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold z-10 ${
                        isActive 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-gray-200 text-gray-500'
                      } ${isCurrent ? 'ring-4 ring-emerald-100' : ''}`}>
                        {stepNum}
                      </div>
                      <span className={`text-xs mt-2 text-center ${isActive ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
                        {status}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Progress Line */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 -z-0" style={{ margin: '0 16%' }}>
                <div 
                  className="h-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${((currentStatus.step - 1) / 3) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Pickup Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Informations de retrait</h2>
          
          {/* Status indicator for active orders */}
          {['Reçue', 'En préparation'].includes(order.status) && (
            <div className="bg-emerald-50 rounded-xl p-4 mb-4 border border-emerald-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center animate-pulse">
                  <ChefHat className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold text-emerald-600">
                    {order.status === 'Reçue' ? 'Commande reçue' : 'En préparation'}
                  </p>
                  <p className="text-sm text-emerald-700">
                    Le restaurant prépare votre commande
                  </p>
                </div>
              </div>
            </div>
          )}

          {order.status === 'Prête' && (
            <div className="bg-emerald-50 rounded-xl p-4 mb-4 border border-emerald-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-lg font-bold text-emerald-600">
                    Commande prête ! 🎉
                  </p>
                  <p className="text-sm text-emerald-700">
                    Venez la récupérer maintenant
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Heure de retrait</p>
                <p className="font-semibold">
                  {formatPickupTime(order.pickup_slot, order.pickup_time)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Restaurant</p>
                <p className="font-semibold">{order.restaurant_name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Détails de la commande</h2>
          
          <div className="divide-y divide-gray-100">
            {order.items.map((item) => (
              <div key={item.id} className="py-3 flex justify-between">
                <div>
                  <span className="font-medium">{item.quantity}x</span>{' '}
                  <span>{item.dish_name || item.name}</span>
                  {item.selected_option_names && item.selected_option_names.length > 0 && (
                    <p className="text-sm text-gray-500">
                      {item.selected_option_names.join(', ')}
                    </p>
                  )}
                  {item.selected_ingredient_names && item.selected_ingredient_names.length > 0 && (
                    <p className="text-sm text-emerald-600">
                      🥗 {item.selected_ingredient_names.join(', ')}
                    </p>
                  )}
                </div>
                <span className="font-medium">{item.total_price?.toFixed(2)} €</span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Sous-total</span>
              <span>{order.subtotal.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Frais de service</span>
              <span>{order.service_fee.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between font-semibold text-lg pt-2">
              <span>Total</span>
              <span className="text-emerald-600">{order.total_amount.toFixed(2)} €</span>
            </div>
          </div>

          {order.special_instructions && (
            <div className="mt-4 p-3 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Instructions spéciales:</p>
              <p className="text-sm">{order.special_instructions}</p>
            </div>
          )}
        </div>

        {/* Payment Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Paiement</h2>
          <div className="flex justify-between">
            <span className="text-gray-500">Mode de paiement</span>
            <span className="font-medium">{order.payment_method}</span>
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-gray-500">Statut</span>
            <Badge className={order.payment_status === 'Payée' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}>
              {order.payment_status}
            </Badge>
          </div>
          
          {/* Download Receipt Button */}
          {order.status !== 'Annulée' && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Button
                onClick={handleDownloadReceipt}
                disabled={downloadingReceipt}
                variant="outline"
                className="w-full rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="download-receipt-btn"
              >
                {downloadingReceipt ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    Téléchargement...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Télécharger le reçu (PDF)
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-400 text-center mt-2">
                Reçu conforme avec détail TVA
              </p>
            </div>
          )}
        </div>

        {/* Review Section - Only for completed orders */}
        {order.status === 'Terminée' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mt-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Votre avis
            </h2>
            
            {existingReview ? (
              // Show existing review
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <StarRating rating={existingReview.rating} readonly size="md" />
                  <span className="text-sm text-gray-500">
                    {new Date(existingReview.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                {existingReview.comment && (
                  <p className="text-gray-600">{existingReview.comment}</p>
                )}
                {existingReview.restaurant_response && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-700">Réponse du restaurant:</p>
                    <p className="text-sm text-gray-600 mt-1">{existingReview.restaurant_response}</p>
                  </div>
                )}
                <p className="text-sm text-emerald-600">✓ Merci pour votre avis !</p>
              </div>
            ) : canReview ? (
              // Show review form
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comment évaluez-vous cette commande ?
                  </label>
                  <StarRating rating={reviewRating} setRating={setReviewRating} size="lg" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Un commentaire ? (optionnel)
                  </label>
                  <Textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Partagez votre expérience..."
                    rows={3}
                    className="rounded-xl"
                  />
                </div>
                
                <Button
                  onClick={handleSubmitReview}
                  disabled={submittingReview}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full"
                >
                  {submittingReview ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer mon avis
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">
                Les avis ne sont disponibles que pour les commandes terminées.
              </p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
